import { IConnectedDevice } from '../../../userManagement/users/serverApi/types/connectedDevices/IConnectedDevice';
import React = require('react');
import { ButtonType, ButtonWidget, Tooltip, TooltipPosition } from '@tableau/widgets';

import { HelpService } from '~commonInfra/services/HelpService';
import { beaconComponentRegistryEntry, getUserAccountSettingsURL } from '~src/beacon/BeaconComponentRegistryFunctions';
import { DeepLink } from '~src/beacon/deepLink/DeepLink';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';

import { Formatters, Messages } from '../../../codegen/Localize';
import { L10n } from '../../../commonInfra/L10n/L10n';
import { ServerUsers } from '../../../userManagement/services/resources/ServerUsers';
import { IServerUser } from '../../../userManagement/users/serverApi/types/IServerUser';
import { showDeleteConnectedDeviceDialog } from '../../services/DeleteConnectedDeviceAction';
import { UserSettingsStyle } from '../../styles/UserSettingsStyle';

const DEEPLINK_ID = 'connected-clients';

export interface ConnectedDeviceSectionProps {
  serverUser: IServerUser;
}

export function ConnectedDevices(props: ConnectedDeviceSectionProps) {
  const [connectedDevices, setConnectedDevices] = React.useState([] as IConnectedDevice[]);
  const DEVICE_NAME_FOR_PERSONAL_ACCESS_TOKENS = 'PERSONAL_ACCESS_TOKEN';
  const DEVICE_NAME_FOR_SERVICE_TOKENS = 'SERVICE_TOKEN';

  async function refreshConnectedDevices() {
    const refreshTokenDevices: IConnectedDevice[] = await ServerUsers.instance.getUserConnectedDevices(props.serverUser.id);
    // Personal Access Tokens are grouped together under a device, but we don't want to show it in the device list.
    const connectedDevices = refreshTokenDevices.filter(x => (x.id !== DEVICE_NAME_FOR_PERSONAL_ACCESS_TOKENS && x.id !== DEVICE_NAME_FOR_SERVICE_TOKENS));
    setConnectedDevices(connectedDevices);
  }

  async function deleteConnectedDevice(device: IConnectedDevice) {
    await showDeleteConnectedDeviceDialog(device, props.serverUser.id);
    refreshConnectedDevices();
  }

  React.useEffect(
    () => {
      refreshConnectedDevices();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <DeepLink deepLinkId={DEEPLINK_ID}>
      <table>
        <tbody data-tb-test-id="connected-client-section">
          <tr>
            <td className={UserSettingsStyle.labelText} data-tb-test-id="usersetting-ConnectedClients-label">
              {Messages.serverConnectedDevices()}
            </td>
            <td data-tb-test-id="usersetting-connectedClients-description" className={UserSettingsStyle.detailsValue}>
              {Messages.connectedDevicesDescription()}
              <a className="tb-lr-padded" rel="noopener noreferrer" target="_blank" href={HelpService.getServerHelpUrlByCategory('manage-cred')}>
                <span>{Messages.connectedDevicesLearnMore()}</span>
              </a>
            </td>
          </tr>
          <tr>
            <td className={UserSettingsStyle.labelText}></td>
            <td className={UserSettingsStyle.detailsValue} colSpan={2}> {connectedDevices && connectedDevices.length > 0 ? renderDeviceList(props, connectedDevices, deleteConnectedDevice) :
              <span data-tb-test-id="no-connected-clients">{Messages.noConnectedDevices()} </span>}</td>
          </tr>
        </tbody>
      </table>
    </DeepLink>
  );
}

function renderPopOver(count: number) {
  return (
    <Tooltip
      afterDelay
      testId={'userSetting-connectedDevices-Popover'}
      position={TooltipPosition.Right}
      maxWidth={200}
      tooltipContent={Messages.connectedDeviceDatasources({
        COUNT: count,
        COUNT_LOC: Formatters.formatCount(count)
      })}
    >
      <div className="tb-header-row-icon-centered tb-icon-help" />
    </Tooltip>
  );
}

function renderDeviceList(props: ConnectedDeviceSectionProps, connectedDevices: IConnectedDevice[], deleteConnectedDevice: (device: IConnectedDevice) => void) {
  return (
    <>
      <div className="tb-user-settings-item-table">
        <table className="tb-static-grid-table tb-static-grid-table-settings-width">
          <tr data-tb-test-id="connected-devices-table-header">
            <th className={UserSettingsStyle.tableHeaderLine}>
              <span className="tb-data-grid-header-text">
                {Messages.connectedDeviceName()}
              </span>
            </th>
            <th className={UserSettingsStyle.tableHeaderLine}>
              <span className="tb-data-grid-header-text">
                {Messages.connectedDeviceLastConnected()}
              </span>
            </th>
            <th className={UserSettingsStyle.tableHeaderLine}>
              <span className="tb-data-grid-header-text">
              </span>
            </th>
          </tr>
          {
            connectedDevices.map((connectedDevice: IConnectedDevice, index) => {
              return (
                <tr key={index} data-tb-test-id="connected-devices-list">
                  <td className={UserSettingsStyle.tableMultilineWithSeparatorRow}>
                    <div className="tb-data-grid-cell-text">
                      <span className={UserSettingsStyle.itemTableName} title={connectedDevice.displayName}>
                        {connectedDevice.displayName}
                        {connectedDevice.remoteAgent && connectedDevice.connectedDatasourcesCount &&
                          renderPopOver(connectedDevice.connectedDatasourcesCount)}
                      </span>
                    </div>
                  </td>
                  <td className={UserSettingsStyle.tableMultilineWithSeparatorRow}>
                    <span className="tb-data-grid-cell-text">{L10n.localizedDateTimeForISO8601FormattedString(connectedDevice.lastUsed)}</span>
                  </td>
                  <td className={UserSettingsStyle.tableMultilineWithSeparatorRow}>
                    <ButtonWidget
                      title={Messages.remove()}
                      buttonType={ButtonType.LowEmphasis}
                      style={{ float: 'right' }}
                      testId={'userSetting-delete-connected-device-' + connectedDevice.displayName}
                      onClick={() => deleteConnectedDevice(connectedDevice)}
                    >
                      <img src={imgPath('trash_14px_small.svg')} alt="" /><span>{' ' + Messages.remove()}</span>
                    </ButtonWidget>
                  </td>
                </tr>
              );
            })
          }
        </table>
      </div>
    </>
  );
}

export const ConnectedClientsSettingBeaconConfig = beaconComponentRegistryEntry({
  command: () => Messages.serverConnectedDevices(),
  deeplinkId: DEEPLINK_ID,
  url: (props) => getUserAccountSettingsURL(props.sessionInfo),
  tags:  () => { return [Messages.connectedDevicesDescription(), Messages.userSettings_Security_Header()]; },
  isAdmin: false
});